import { AlertTriangle, Lock, RotateCw, SearchX, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { Button } from '../ui/button';
import { LearnMore } from './learn-more';

export interface ErrorCopy {
  kind: 'offline' | 'signed-out' | 'forbidden' | 'not-found' | 'busy' | 'server' | 'crash' | 'other';
  title: string;
  body: string;
  /** Trying again can help. */
  retry: boolean;
  /** Server reference to quote to an admin (Cloudflare request id). */
  reference?: string;
}

/**
 * What an error means for the person, in plain language (PLAN §12: every screen explains the next step). Server
 * messages are shown when the API gave one for a request the person can fix (409, 400); internals never are.
 */
export function errorCopy(error: unknown): ErrorCopy {
  if (error instanceof ApiError) {
    const reference = typeof error.data.requestId === 'string' ? error.data.requestId : undefined;
    if (error.status === 0)
      return {
        kind: 'offline',
        title: 'Hoopoe can’t be reached',
        body: 'You may be offline, or the connection dropped. Check your connection, then try again.',
        retry: true,
      };
    if (error.status === 401)
      return { kind: 'signed-out', title: 'Your session has ended', body: 'Sign in again to continue where you left off.', retry: false };
    if (error.status === 403)
      return {
        kind: 'forbidden',
        title: 'You don’t have access to this',
        body: 'Your role doesn’t include this. Ask an admin if you need it.',
        retry: false,
      };
    if (error.status === 404)
      return {
        kind: 'not-found',
        title: 'This doesn’t exist (any more)',
        body: 'It may have been removed or merged into another record, or the link is wrong.',
        retry: false,
      };
    if (error.status === 429) return { kind: 'busy', title: 'Too many requests', body: 'Wait a minute, then try again.', retry: true };
    if (error.status >= 500)
      return {
        kind: 'server',
        title: 'Something went wrong on our side',
        body: 'Nothing you did caused this. Try again; if it keeps happening, tell an admin and quote the reference.',
        retry: true,
        reference,
      };
    return { kind: 'other', title: 'That didn’t work', body: error.message, retry: true, reference };
  }
  return {
    kind: 'crash',
    title: 'This screen hit a problem',
    body: 'Try again. If it keeps happening, reload the page or go back to Home; your saved work is not affected.',
    retry: true,
  };
}

const ICONS = { offline: WifiOff, 'signed-out': Lock, forbidden: Lock, 'not-found': SearchX } as const;

/** A failed load, in place of the content: what happened, what to do, and a way to try again. */
export function ErrorState({
  error,
  title,
  onRetry,
  learnMore,
  children,
  compact = false,
}: {
  error: unknown;
  /** Overrides the generic title, e.g. "The directory couldn’t be loaded". */
  title?: string;
  onRetry?: () => void;
  learnMore?: string;
  children?: ReactNode;
  /** Inline (inside a panel) rather than a full-page block. */
  compact?: boolean;
}) {
  const copy = errorCopy(error);
  const Icon = ICONS[copy.kind as keyof typeof ICONS] ?? AlertTriangle;
  const next = typeof window === 'undefined' ? '/' : window.location.pathname + window.location.search;
  return (
    <div
      role="alert"
      data-error-kind={copy.kind}
      className={
        compact
          ? 'flex flex-col items-start gap-2 rounded-md border border-danger/40 bg-surface p-3 text-sm'
          : 'flex flex-col items-center gap-3 rounded-lg border border-dashed bg-surface px-6 py-12 text-center'
      }
    >
      <Icon className={compact ? 'size-4 text-danger-foreground' : 'size-8 text-muted-foreground'} aria-hidden="true" />
      <h2 className={compact ? 'font-medium' : 'text-base font-semibold'}>{title ?? copy.title}</h2>
      <p className="max-w-md text-sm text-muted-foreground">{title ? `${copy.title}. ${copy.body}` : copy.body}</p>
      {copy.reference && (
        <p className="text-xs text-muted-foreground">
          Reference: <span className="select-all font-mono">{copy.reference}</span>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {copy.retry && onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry}>
            <RotateCw aria-hidden="true" /> Try again
          </Button>
        )}
        {copy.kind === 'signed-out' && (
          <Button size="sm" asChild>
            <a href={`/login?next=${encodeURIComponent(next)}`}>Sign in again</a>
          </Button>
        )}
        {children}
      </div>
      {learnMore && <LearnMore slug={learnMore} />}
    </div>
  );
}
